import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { fetchAuthTree } from './fetchAuthTree';
import type { AuthOrg, AuthTree } from './types';
import {
  getWorkspace,
  setOrg as storeSetOrg,
  setPortfolio as storeSetPortfolio,
  subscribeWorkspace,
} from './workspaceStore';

function isSelectableOrg(org: AuthOrg): boolean {
  if (!org?.org_id || org.org_id === '_all' || org.portfolio_scope) return false;
  if (org.active === false) return false;
  return true;
}

function orgsForPortfolio(tree: AuthTree | null, portfolioId: string): AuthOrg[] {
  if (!tree?.portfolios || !portfolioId) return [];
  const portfolio = tree.portfolios[portfolioId];
  if (!portfolio?.orgs) return [];
  return Object.values(portfolio.orgs)
    .filter(isSelectableOrg)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

function resolveInitialOrg(orgs: AuthOrg[], preferred: string): string {
  if (preferred && orgs.some((o) => o.org_id === preferred)) return preferred;
  return orgs[0]?.org_id || '';
}

interface WorkspaceContextValue {
  tree: AuthTree | null;
  orgs: AuthOrg[];
  portfolio: string;
  org: string;
  selectedOrg: AuthOrg | null;
  loading: boolean;
  error: string | null;
  setOrg: (orgId: string) => void;
  reloadTree: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [tree, setTree] = useState<AuthTree | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState(getWorkspace);

  useEffect(() => subscribeWorkspace(() => setWorkspace(getWorkspace())), []);

  const applyTree = useCallback((data: AuthTree) => {
    setTree(data);
    const { portfolio: currentPortfolio, org: currentOrg } = getWorkspace();
    let portfolioId = currentPortfolio;
    if (!portfolioId || !data.portfolios?.[portfolioId]) {
      portfolioId = Object.keys(data.portfolios || {})[0] || '';
      if (portfolioId) storeSetPortfolio(portfolioId);
    }
    const orgs = orgsForPortfolio(data, portfolioId);
    const nextOrg = resolveInitialOrg(orgs, currentOrg);
    if (nextOrg && nextOrg !== currentOrg) {
      storeSetOrg(nextOrg);
    }
  }, []);

  const reloadTree = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAuthTree();
      applyTree(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setTree(null);
    } finally {
      setLoading(false);
    }
  }, [applyTree]);

  useEffect(() => {
    void reloadTree();
  }, [reloadTree]);

  const setOrg = useCallback((orgId: string) => {
    storeSetOrg(orgId);
  }, []);

  const orgs = useMemo(
    () => orgsForPortfolio(tree, workspace.portfolio),
    [tree, workspace.portfolio]
  );

  const selectedOrg = useMemo(
    () => orgs.find((o) => o.org_id === workspace.org) || null,
    [orgs, workspace.org]
  );

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      tree,
      orgs,
      portfolio: workspace.portfolio,
      org: workspace.org,
      selectedOrg,
      loading,
      error,
      setOrg,
      reloadTree,
    }),
    [tree, orgs, workspace.portfolio, workspace.org, selectedOrg, loading, error, setOrg, reloadTree]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return ctx;
}
